import {BaseViewStore, BaasicDropdownStore, TableViewStore} from 'core/stores';
import applicationContext from 'core/utils/applicationContext';
import {FacilityService} from 'application/facility/services';
import {FacilityPumpService} from 'application/pumps/services';
import { action, observable, runInAction } from 'mobx';
import { QueryUtility } from 'core/utils';
import {GridBooleanCell} from 'core/components';
import { toDate } from 'core/utils';
import { ModalParams } from 'core/models';
import _ from 'lodash';
@applicationContext
class PumpViewStore extends BaseViewStore {
    @observable accessor initialFacility = false;
    @observable accessor pumpStatus = [];
    @observable accessor activeNumber = null;
    @observable accessor selectedStatusId = null;   
    @observable accessor selectedFacility = null;

    constructor(rootStore) {
        super(rootStore);

        this.facilityService = new FacilityService(rootStore.application.baasic.apiClient);
        this.facilityPumpService = new FacilityPumpService(rootStore.application.baasic.apiClient);

        this.hasPermission = (authorization) => {
            return this.rootStore.permissionStore.hasPermission(authorization);
        };

        this.authorization = {
            create: this.hasPermission('pumpTrackingSection.create'),
            update: this.hasPermission('pumpTrackingSection.update'),
            delete: this.hasPermission('pumpTrackingSection.delete')            
        };

        this.facilityStore = new BaasicDropdownStore({
            filterable: true
        }, {
            fetchFunc: async (term) => {
                const response = await this.facilityService.find({ searchQuery: term, rpp: 1000 });
                return response.data.item;
            }
        });

        this.fetch[this.getStatus()];

        this.goToFacility = () => {
            this.initialFacility = true;
            this.getActiveNumber(this.queryUtility.filter.facilityId);
            this.getSelectedFacility(this.queryUtility.filter.facilityId);
            this.queryUtility.fetch();
        };

        this.onStatusFilter = (statusId) => {
            if(statusId == this.selectedStatusId){
                this.selectedStatusId = null;
                delete this.queryUtility.filter.statusId;                
            }else{
                this.queryUtility.filter.set('statusId', statusId);
                this.selectedStatusId = statusId;
            }

            this.queryUtility.fetch();            
        };

        this.queryUtility = new QueryUtility(this.rootStore, async filter => {
            this.tableStore.suspend();
            const response = await this.getFacilityPumps(filter);
            this.tableStore.setData(response);
            this.tableStore.resume();
        }, {});

        let actions = {};
        if(this.authorization.delete){
            actions.onDelete =  this.deleteResource;
        }
        if(this.authorization.update){
            actions.onStatusChange =  this.openStatusChangeModal;
            actions.onEdit = this.openEditModal
        }

        this.tableStore = new TableViewStore(this.queryUtility, {
            columns: [
                {
                    key: 'product',
                    title: 'PUMPS.LIST.COLUMNS.PRODUCT'
                },
                {
                    key: 'serialNumber',
                    title: 'PUMPS.LIST.COLUMNS.SERIAL_NUMBER'
                },
                {
                    key: 'stickered',
                    title: 'PUMPS.LIST.COLUMNS.STICKERED',
                    cell: GridBooleanCell,
                    headerClassName: 'center'
                },
                {
                    key: 'dateSent',
                    title: 'PUMPS.LIST.COLUMNS.DATE_SENT'
                },
                {
                    key: 'pumpStatus',
                    title: 'PUMPS.LIST.COLUMNS.STATUS'
                },
                {
                    key: 'dateReturnRequested',
                    title: 'PUMPS.LIST.COLUMNS.DATE_RETURN_REQUESTED'
                },
                {
                    key: 'dateReturned',
                    title: 'PUMPS.LIST.COLUMNS.DATE_RETURNED'
                }
            ],
            actions: actions
        });

        if(rootStore.routerStore.router.routerState.queryParams.facilityId){
            this.selectedStatusId = rootStore.routerStore.router.routerState.queryParams.statusId;
            this.fetch([this.queryUtility.initialize()]);
            this.initialFacility = true;            
            this.queryUtility.fetch();
            this.getActiveNumber(this.queryUtility.filter.facilityId);    
            this.getSelectedFacility(this.queryUtility.filter.facilityId);
        }

        this.statusChangeModal = new ModalParams({
            onClose: () => {
                this.queryUtility.fetch();
                this.getActiveNumber(this.queryUtility.filter.facilityId);       
            }
        });

        this.editModal = new ModalParams({
            onClose: () => {
                this.queryUtility.fetch();
                this.getActiveNumber(this.queryUtility.filter.facilityId);       
            }
        });
    }

    @action.bound
    async onCreate() {
        await this.queryUtility.fetch();
        await this.getActiveNumber(this.queryUtility.filter.facilityId);
    }

    @action.bound
    async openEditModal(item) {
        this.editModal.open({ 
            facilityId: this.queryUtility.filter.facilityId,
            id: item.id
        });
    }

    @action.bound
    async openStatusChangeModal(item) {
        this.statusChangeModal.open({ 
            item: item
        });
    }

    @action.bound
    async getStatus() {
        const response = await this.facilityPumpService.getStatus();
        runInAction(() => {
            this.pumpStatus = response;
        });
    }

    @action.bound
    async getSelectedFacility(facilityId) {
        const response = await this.facilityService.get(facilityId);
        runInAction(() => {
            this.selectedFacility = response.data;
        });
    }

    @action.bound
    async getActiveNumber(facilityId) {
        const response = await this.facilityPumpService.getActiveNumber(facilityId);
        runInAction(() => {
            this.activeNumber = response;
        });
    }

    @action.bound
    async getFacilityPumps(filter) {
        var params = _.extend(filter, { embed: 'Product,PumpStatus' });
        const response = await this.facilityPumpService.find(params);
        let items = _.map(response.data.item, item => {
            return _.extend(item, {
                status: item.pumpStatus,
                pumpStatus: item.pumpStatus.name,
                product: item.product.itemId,
                dateSent: toDate(item.dateSent),
                dateReturnRequested: toDate(item.dateReturnRequested),
                dateReturned: toDate(item.dateReturned)              
            });
        });

        response.data.item = items;
        return response.data;
    }

    @action.bound
    async deleteResource(item) {
        const { modalStore, notificationStore } = this.rootStore;

        modalStore.showConfirm(
            'PUMPS.DELETE.PUMP',
            async () => {
                try {
                    await this.facilityPumpService.delete(item);
                    await this.queryUtility.fetch();
                    await this.getActiveNumber(this.queryUtility.filter.facilityId);
                    await setTimeout(() => this.rootStore.notificationStore.success("PUMPS.DELETE.SUCCESS"));
                } catch (err) {
                    notificationStore.error('LIST_LAYOUT.ERROR_DELETE', err);
                }
            }
        );
    }

    @action.bound
    async openFacility(facilityId) {
        window.open(`${this.rootStore.routerStore .findRoute('master.app.main.facility.preview').pattern.replace(":id", facilityId)}`);
    }

    @action.bound
    openEditNotePopup(id) {
        this.editNoteModal.open({
            id: id,
            facilityId: this.selectedFacility.id,
            fetchTable: () => {
                this.getNotes();
            }
        });
    }
}

export default PumpViewStore;
